import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DetailHeader from '../../components/JobBoard/Admin/Detail/DetailHeader';
import DetailContent from '../../components/JobBoard/Admin/Detail/DetailContent';
import {
  buildJobOptionMeta,
  getJobOptionCode,
  mapJobPostDetailToCardModel,
  Modal,
  Toast,
  useT,
} from '@repo/common';
import {
  changeJobPostStatus,
  closeJobPost,
  deleteJobPost,
  deleteJobPostPermanent,
  duplicateJobPost,
  getJobPostDetail,
  getJobPostOptions,
  hideJobPost,
  unhideJobPost,
} from '../../api/jobboard/jobboard.api';

const JobBoardAdminDetail = () => {
  const t = useT();
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [optionMeta, setOptionMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const [pageToast, setPageToast] = useState(null);

  useEffect(() => {
    if (!location.state?.toast) return;
    setPageToast(location.state.toast);
    navigate(location.pathname, { replace: true });
  }, [location, navigate]);

  const loadDetail = useCallback(async () => {
    if (!jobId) return;
    try {
      setIsLoading(true);
      const [detail, optionGroups] = await Promise.all([
        getJobPostDetail(jobId),
        getJobPostOptions(),
      ]);
      const optionMetaData = buildJobOptionMeta(optionGroups);
      setOptionMeta(optionMetaData);

      const cardModel = mapJobPostDetailToCardModel(detail, optionMetaData);
      const responseSpeedCode = getJobOptionCode(
        optionMetaData,
        detail.responseSpeedOptionItemId,
      );

      setJob({
        ...detail,
        ...cardModel,
        company: detail.companyName,
        titleChips: responseSpeedCode === 'WITHIN_24H' ? ['quickResponse'] : [],
      });
    } catch (error) {
      console.error('Failed to load admin job detail:', error);
      setJob(null);
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleAction = async (action) => {
    if (!job) return;

    try {
      switch (action) {
        case 'delete':
          await deleteJobPost(job.id);
          navigate('/jobs/admin', { state: { toast: { action: 'delete' } } });
          return;
        case 'hide':
          await hideJobPost(job.id);
          navigate('/jobs/admin', { state: { toast: { action: 'hide' } } });
          return;
        case 'unhide':
        case 'setActive':
        case 'reopen':
          await unhideJobPost(job.id).catch(async () => {
            await changeJobPostStatus(job.id, 'ACTIVE');
          });
          navigate('/jobs/admin', { state: { toast: { action } } });
          return;
        case 'close':
          await closeJobPost(job.id);
          navigate('/jobs/admin', { state: { toast: { action: 'close' } } });
          return;
        case 'duplicate':
          await duplicateJobPost(job.id);
          navigate('/jobs/admin', { state: { toast: { action: 'duplicate' } } });
          return;
        case 'publish':
          setActiveModal('publish');
          return;
        case 'deletePermanent':
          setActiveModal('deletePermanent');
          return;
        case 'restore':
          await changeJobPostStatus(job.id, 'ACTIVE');
          navigate('/jobs/admin', { state: { toast: { action: 'restore' } } });
          return;
        default:
          return;
      }
    } catch (error) {
      console.error(`Failed to handle admin action(${action}):`, error);
    }
  };

  const handleConfirmPublish = async () => {
    if (!job) return;
    try {
      await changeJobPostStatus(job.id, 'ACTIVE');
      navigate('/jobs/admin', { state: { toast: { action: 'publish' } } });
    } catch (error) {
      console.error('Publish failed:', error);
    } finally {
      setActiveModal(null);
    }
  };

  const handleConfirmDeletePermanent = async () => {
    if (!job) return;
    try {
      await deleteJobPostPermanent(job.id);
      navigate('/jobs/admin', { state: { toast: { action: 'deletePermanent' } } });
    } catch (error) {
      console.error('Delete permanently failed:', error);
    } finally {
      setActiveModal(null);
    }
  };

  if (isLoading) {
    return (
      <div className='bg-grayscale-gray01 tabletH:bg-grayscale-gray00 flex flex-col items-center px-0 pb-[120px] tabletH:px-[80px] tabletH:pb-[240px] min-h-screen w-full relative'>
        <div className='pt-[120px] typo-b18Strong text-text-secondary'>
          {t('Loading job detail...', '공고 정보를 불러오는 중...')}
        </div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className='bg-grayscale-gray01 tabletH:bg-grayscale-gray00 flex flex-col items-center px-0 pb-[120px] tabletH:px-[80px] tabletH:pb-[240px] min-h-screen w-full relative'>
      <DetailHeader job={job} onAction={handleAction} />
      <DetailContent job={job} optionMeta={optionMeta} />

      {pageToast?.action === 'publish' && (
        <div className='fixed bottom-6 left-0 right-0 z-[1000] flex justify-center px-4'>
          <Toast
            type='success'
            state='white'
            title={t('Job published', '공고를 게시했습니다')}
            direction='up'
            customWidth='auto'
            onClose={() => setPageToast(null)}
          />
        </div>
      )}

      {activeModal === 'deletePermanent' && (
        <Modal
          isOpen
          title={t(
            'Delete this job permanently?',
            '이 공고를 영구 삭제할까요?',
          )}
          subText={t(
            'This job will be permanently deleted and cannot be restored',
            '영구 삭제된 공고는 복원할 수 없습니다',
          )}
          onClickLeftBtn={() => setActiveModal(null)}
          onClickRightBtn={handleConfirmDeletePermanent}
          rightButtonLabel={t('Delete permanently', '영구 삭제')}
        />
      )}
      {activeModal === 'publish' && (
        <Modal
          isOpen
          title={t('Publish this job?', '이 공고를 게시할까요?')}
          subText={t(
            'Once published, this job will become active and visible to users',
            '게시하면 공고가 활성화되어 사용자에게 노출됩니다',
          )}
          onClickLeftBtn={() => setActiveModal(null)}
          onClickRightBtn={handleConfirmPublish}
          rightButtonLabel={t('Publish Job', '공고 게시')}
        />
      )}
    </div>
  );
};

export default JobBoardAdminDetail;
